import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Link2, CheckCircle2, Copy, Check, AlertTriangle, Inbox, Search,
  Lock, Send, MessageSquare, EyeOff, Plus, Paperclip, Star,
} from 'lucide-react';
import BrandMark from './BrandMark';

/* ------------------------------------------------------------------ *
 * Identidade visual — ReportFIUS (ver design-system/tokens.css)
 * Navy #031B31 · Ciano #009EDB · Laranja (só CTA) #EA5431 · Fundo #F3F3F3
 * ------------------------------------------------------------------ */

const STATUS = {
  received: {
    label: 'Recebida',
    icon: Inbox,
    tone: 'bg-[#009EDB]/10 text-[#00688F] ring-[#009EDB]/30',
    dot: 'bg-[#009EDB]',
    description: 'Sua denúncia foi recebida e está aguardando análise inicial.',
  },
  investigating: {
    label: 'Em investigação',
    icon: Search,
    tone: 'bg-amber-50 text-amber-800 ring-amber-300/60',
    dot: 'bg-amber-500',
    description: 'Sua denúncia está sendo investigada pela equipe responsável.',
  },
  resolved: {
    label: 'Resolvida',
    icon: CheckCircle2,
    tone: 'bg-emerald-50 text-emerald-800 ring-emerald-300/60',
    dot: 'bg-emerald-500',
    description: 'Sua denúncia foi analisada e as medidas necessárias foram tomadas.',
  },
  closed: {
    label: 'Encerrada',
    icon: Lock,
    tone: 'bg-slate-100 text-slate-700 ring-slate-300',
    dot: 'bg-slate-400',
    description: 'Caso encerrado.',
  },
};

const PAGE = 'min-h-screen bg-[#F3F3F3] font-[Mulish,Inter,system-ui,sans-serif] text-[#0B2942] antialiased';
const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-black/5';

/** Barra superior com a marca — mesma assinatura da landing. */
const TopBar = () => (
  <header className="bg-[#031B31]">
    <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5 sm:px-8">
      <a href="/" className="flex items-center gap-2.5 text-lg font-black tracking-tight text-white">
        <BrandMark tone="dark" height={34} decorative />
        <span>
          REPORT<span className="font-light text-[#5BC5EE]">FIUS</span>
        </span>
      </a>
      <span className="text-xs font-semibold uppercase tracking-widest text-white/60">
        Canal de denúncias
      </span>
    </div>
  </header>
);

const ComplaintTracker = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  // Só na chegada do formulário. Guardado em estado porque o ?novo=1 é apagado
  // logo abaixo — assim quem favorita a página salva o link limpo.
  const [isNew] = useState(() => searchParams.get('novo') === '1');
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [copied, setCopied] = useState('');

  const trackingUrl = `${window.location.origin}/track/${token}`;

  useEffect(() => {
    if (isNew) window.history.replaceState(null, '', `/track/${token}`);
  }, [isNew, token]);

  const copyToClipboard = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback para navegadores sem clipboard API (ou contexto não seguro)
      const input = document.createElement('textarea');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(kind);
    setTimeout(() => setCopied(''), 2500);
  };

  // Carregar dados da denúncia
  useEffect(() => {
    const loadComplaint = async () => {
      if (!token) {
        setError('Token de acompanhamento não fornecido');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/complaints/track/${token}`);
        if (response.data.success) {
          setComplaint(response.data.complaint);
        }
      } catch (error) {
        console.error('Erro ao carregar denúncia:', error);
        if (error.response?.status === 404) {
          setError('Denúncia não encontrada. Confira se o link foi copiado inteiro, sem faltar nenhum caractere no final.');
        } else {
          setError('Erro ao carregar dados da denúncia.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadComplaint();
  }, [token]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/complaints/track/${token}/message`, {
        message: newMessage
      });

      if (response.data.success) {
        // Atualizar lista de mensagens
        setComplaint(prev => ({
          ...prev,
          messages: [...prev.messages, {
            id: Date.now(),
            message: newMessage,
            is_from_admin: false,
            created_at: new Date().toISOString()
          }]
        }));
        setNewMessage('');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={PAGE}>
        <TopBar />
        <div className="flex min-h-[60vh] items-center justify-center px-5">
          <div className={`${CARD} flex flex-col items-center gap-4 p-10`}>
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#009EDB]/25 border-t-[#009EDB]" />
            <p className="text-sm text-[#38495A]">Carregando sua denúncia…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={PAGE}>
        <TopBar />
        <div className="mx-auto flex min-h-[60vh] max-w-lg items-center px-5 sm:px-8">
          <div className={`${CARD} w-full p-8 text-center`}>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="h-7 w-7 text-red-500" />
            </span>
            <h1 className="mt-5 text-2xl font-light tracking-[-0.02em] text-[#0B2942]">
              Não foi possível abrir esta denúncia
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-[#38495A]">{error}</p>
            <a
              href="/"
              className="mt-6 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-[#EA5431] px-7 py-3 text-sm font-bold text-white shadow-lg shadow-[#EA5431]/25 transition hover:bg-[#d94826]"
            >
              Voltar ao início
            </a>
          </div>
        </div>
      </div>
    );
  }

  const status = STATUS[complaint?.status] || STATUS.received;
  const StatusIcon = status.icon;
  const messages = complaint?.messages || [];

  const facts = [
    ['Canal', complaint?.form_title || '—'],
    ['Enviada em', formatDate(complaint?.submitted_at || new Date())],
    ['Última atualização', formatDate(complaint?.last_update || new Date())],
    ['Mensagens', String(complaint?.message_count ?? messages.length)],
  ];

  return (
    <div className={PAGE}>
      <TopBar />

      <main className="mx-auto w-full max-w-5xl px-5 pb-16 pt-8 sm:px-8 sm:pt-12">
        {/* ---------------- Link de acompanhamento (informação principal) --------------- */}
        <section className={`${CARD} overflow-hidden`}>
          {isNew && (
            <div className="flex items-start gap-3 bg-gradient-to-br from-[#031B31] to-[#0A3355] px-6 py-5 sm:px-8">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#009EDB]">
                <Check className="h-5 w-5 text-white" strokeWidth={3} />
              </span>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-white sm:text-xl">
                  Denúncia enviada com sucesso
                </h1>
                <p className="mt-0.5 text-sm leading-relaxed text-white/70">
                  Antes de fechar esta página, guarde o link abaixo.
                </p>
              </div>
            </div>
          )}

          <div className="p-6 sm:p-8">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#009EDB]">
              <Link2 className="h-4 w-4" />
              Seu link de acompanhamento
            </p>
            <h2 className="mt-2 text-[26px] font-light leading-[1.15] tracking-[-0.02em] text-[#0B2942] sm:text-[32px]">
              {isNew ? 'Guarde este endereço' : 'Este é o seu link de acompanhamento'}
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#38495A] sm:text-base">
              É por ele que você volta a esta página para ver o andamento da denúncia e conversar
              com a equipe — sem precisar se identificar.
            </p>

            <div className="mt-5 rounded-2xl bg-[#F3F3F3] p-4 ring-1 ring-black/5 sm:p-5">
              <p
                className="select-all break-all font-mono text-[13px] leading-relaxed text-[#0B2942] sm:text-sm"
                title="Toque para selecionar"
              >
                {trackingUrl}
              </p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => copyToClipboard(trackingUrl, 'link')}
                  className={`inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-full px-6 text-sm font-bold text-white shadow-lg transition ${
                    copied === 'link'
                      ? 'bg-emerald-600 shadow-emerald-600/25'
                      : 'bg-[#EA5431] shadow-[#EA5431]/25 hover:bg-[#d94826]'
                  }`}
                >
                  {copied === 'link' ? (
                    <><Check className="h-4 w-4" strokeWidth={3} /> Link copiado</>
                  ) : (
                    <><Copy className="h-4 w-4" /> Copiar link</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => copyToClipboard(token, 'token')}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-white px-6 text-sm font-bold text-[#0B2942] ring-1 ring-black/10 transition hover:bg-[#0B2942]/[0.04]"
                >
                  {copied === 'token' ? (
                    <><Check className="h-4 w-4 text-emerald-600" strokeWidth={3} /> Código copiado</>
                  ) : (
                    <><Copy className="h-4 w-4" /> Copiar só o código</>
                  )}
                </button>
              </div>
            </div>

            <ul className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                [Star, 'Salve nos favoritos', 'Ou cole o link em um lugar seguro só seu.'],
                [EyeOff, 'Não vai por e-mail', 'Para preservar seu anonimato, não enviamos este link. Perdeu, não há como recuperar.'],
                [Lock, 'Não compartilhe', 'Quem tiver o link consegue ver a denúncia.'],
              ].map(([Icon, title, text]) => (
                <li key={title} className="flex gap-3 rounded-xl bg-[#009EDB]/[0.06] p-4 sm:flex-col sm:gap-2">
                  <Icon className="h-5 w-5 shrink-0 text-[#009EDB]" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#0B2942]">{title}</p>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-[#38495A]">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* --------------------------- Status + comunicação --------------------------- */}
        <div className="mt-6 grid gap-6 lg:grid-cols-5">
          {/* Status */}
          <section className={`${CARD} min-w-0 p-6 sm:p-8 lg:col-span-2`}>
            <h2 className="text-xs font-bold uppercase tracking-widest text-[#009EDB]">
              Status atual
            </h2>

            <div className="mt-4 flex items-center gap-3">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ring-1 ${status.tone}`}>
                <StatusIcon className="h-6 w-6" />
              </span>
              <div className="min-w-0">
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold ring-1 ${status.tone}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                  {status.label}
                </span>
              </div>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#38495A]">{status.description}</p>

            <dl className="mt-6 space-y-3 border-t border-black/5 pt-5">
              {facts.map(([label, value]) => (
                <div key={label} className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                  <dt className="shrink-0 text-[13px] text-[#38495A]">{label}</dt>
                  <dd className="min-w-0 break-words text-sm font-semibold text-[#0B2942] sm:text-right">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>

            {complaint?.attachment_message && (
              <p className="mt-5 flex gap-2 rounded-xl bg-[#009EDB]/[0.06] p-4 text-[13px] leading-relaxed text-[#0B2942]">
                <Paperclip className="h-4 w-4 shrink-0 text-[#009EDB]" />
                <span className="min-w-0">{complaint.attachment_message}</span>
              </p>
            )}
          </section>

          {/* Comunicação */}
          <section className={`${CARD} flex min-w-0 flex-col p-6 sm:p-8 lg:col-span-3`}>
            <h2 className="text-xs font-bold uppercase tracking-widest text-[#009EDB]">
              Comunicação
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#38495A]">
              Converse com a equipe responsável. As mensagens são anônimas: nada aqui identifica você.
            </p>

            <div className="mt-5 max-h-96 space-y-3 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-black/10 px-5 py-10 text-center">
                  <MessageSquare className="mx-auto h-8 w-8 text-[#009EDB]/40" />
                  <p className="mt-3 text-sm font-semibold text-[#0B2942]">Nenhuma mensagem ainda</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-[#38495A]">
                    Se quiser acrescentar algo à denúncia, escreva abaixo.
                  </p>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.is_from_admin ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 sm:max-w-[75%] ${
                        message.is_from_admin
                          ? 'bg-[#F3F3F3] text-[#0B2942]'
                          : 'bg-[#031B31] text-white'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                        {message.message}
                      </p>
                      <p className={`mt-1.5 text-[11px] ${message.is_from_admin ? 'text-[#38495A]' : 'text-white/60'}`}>
                        {message.is_from_admin ? 'Equipe' : 'Você'} · {formatDate(message.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleSendMessage} className="mt-5">
              <label htmlFor="nova-mensagem" className="text-sm font-bold text-[#0B2942]">
                Enviar nova mensagem
              </label>
              <textarea
                id="nova-mensagem"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Digite sua mensagem…"
                rows={3}
                disabled={sendingMessage}
                className="mt-2 w-full resize-none rounded-2xl border-0 bg-[#F3F3F3] px-4 py-3 text-sm text-[#0B2942] ring-1 ring-black/5 transition placeholder:text-[#38495A]/60 focus:outline-none focus:ring-2 focus:ring-[#009EDB] disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={sendingMessage || !newMessage.trim()}
                className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-[#031B31] px-6 text-sm font-bold text-white transition hover:bg-[#0A3355] disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-8"
              >
                {sendingMessage ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Enviando…
                  </>
                ) : (
                  <><Send className="h-4 w-4" /> Enviar mensagem</>
                )}
              </button>
            </form>
          </section>
        </div>

        <div className="mt-8 text-center">
          <a
            href="/"
            className="inline-flex min-h-[44px] items-center justify-center gap-2 text-sm font-bold text-[#0B2942] transition hover:text-[#009EDB]"
          >
            <Plus className="h-4 w-4" />
            Fazer nova denúncia
          </a>
        </div>
      </main>
    </div>
  );
};

export default ComplaintTracker;
